import React, { useEffect, useMemo, useState } from 'react';
import { listGoals, listProgress, upsertGoal, upsertProgress, IntakeGoal, IntakeProgress } from '../api/intake';
import { ProtectedLayout } from '../components/layout/ProtectedLayout';
import { AuthenticatedUser } from '../components/layout/NavigationDrawer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const IntakeTrackerPage: React.FC = () => (
  <ProtectedLayout>
    {({ user }) => <IntakeTrackerContent user={user} />}
  </ProtectedLayout>
);

interface IntakeTrackerContentProps {
  user: AuthenticatedUser;
}

function IntakeTrackerContent({ user }: IntakeTrackerContentProps) {
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayISO());
  const [goals, setGoals] = useState<IntakeGoal[]>([]);
  const [progresses, setProgresses] = useState<IntakeProgress[]>([]);

  const goalForDate = useMemo(() => goals.find(g => g.date === date), [goals, date]);
  const progressForDate = useMemo(() => progresses.find(p => p.date === date), [progresses, date]);

  const [waterGoal, setWaterGoal] = useState<number>(0);
  const [foodGoal, setFoodGoal] = useState<number>(0);
  const [waterConsumed, setWaterConsumed] = useState<number>(0);
  const [foodConsumed, setFoodConsumed] = useState<number>(0);

  useEffect(() => {
    setWaterGoal(goalForDate?.water_goal_ml || 0);
    setFoodGoal(goalForDate?.food_goal_g || 0);
    setWaterConsumed(progressForDate?.water_consumed_ml || 0);
    setFoodConsumed(progressForDate?.food_consumed_g || 0);
  }, [goalForDate, progressForDate]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [g, p] = await Promise.all([listGoals(), listProgress()]);
        setGoals(g);
        setProgresses(p);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveGoal = async () => {
    const saved = await upsertGoal(date, waterGoal, foodGoal, goalForDate?.id);
    setGoals(prev => {
      const existing = prev.find(x => x.id === saved.id);
      const others = prev.filter(x => x.id !== saved.id);
      // If it was a new goal (no id before), also remove any same-date stale item
      const deduped = others.filter(x => x.date !== saved.date);
      return [saved, ...deduped];
    });
    if (saved.date === date) {
      setWaterGoal(saved.water_goal_ml);
      setFoodGoal(saved.food_goal_g);
    }
  };

  const saveProgress = async () => {
    const saved = await upsertProgress(date, waterConsumed, foodConsumed, progressForDate?.id);
    setProgresses(prev => {
      const others = prev.filter(x => x.id !== saved.id);
      return [saved, ...others];
    });
  };

  const waterPct = Math.min(100, Math.round(((progressForDate?.water_consumed_ml || 0) / Math.max(1, goalForDate?.water_goal_ml || 0)) * 100));
  const foodPct = Math.min(100, Math.round(((progressForDate?.food_consumed_g || 0) / Math.max(1, goalForDate?.food_goal_g || 0)) * 100));

  if (loading) return <div>Loading...</div>;

  const isAdmin = !!user?.is_staff || user?.username === 'admin';
  const isKitten = user?.username === 'Kitten';

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-xl font-semibold">Daily Intake Tracker</h1>

      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Date</label>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-auto" />
      </div>

      {isAdmin && (
        <section className="space-y-3 border rounded-md p-4">
          <h2 className="font-medium">Set Goal (Admin)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm mb-1">Water goal (ml)</label>
              <Input type="number" min={0} value={waterGoal} onChange={e => setWaterGoal(parseInt(e.target.value || '0', 10))} />
            </div>
            <div>
              <label className="block text-sm mb-1">Food goal (g)</label>
              <Input type="number" min={0} value={foodGoal} onChange={e => setFoodGoal(parseInt(e.target.value || '0', 10))} />
            </div>
            <div className="flex items-end">
              <Button onClick={saveGoal} className="w-full">Save Goal</Button>
            </div>
          </div>
          {goalForDate && <p className="text-xs text-gray-500">Last updated: {new Date(goalForDate.updated_at).toLocaleString()}</p>}
        </section>
      )}

      {isKitten && (
        <section className="space-y-3 border rounded-md p-4">
          <h2 className="font-medium">Update Progress (Kitten)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm mb-1">Water consumed (ml)</label>
              <Input type="number" min={0} value={waterConsumed} onChange={e => setWaterConsumed(parseInt(e.target.value || '0', 10))} />
            </div>
            <div>
              <label className="block text-sm mb-1">Food consumed (g)</label>
              <Input type="number" min={0} value={foodConsumed} onChange={e => setFoodConsumed(parseInt(e.target.value || '0', 10))} />
            </div>
            <div className="flex items-end">
              <Button onClick={saveProgress} className="w-full">Save Progress</Button>
            </div>
          </div>
          {progressForDate && <p className="text-xs text-gray-500">Last updated: {new Date(progressForDate.updated_at).toLocaleString()}</p>}
        </section>
      )}

      <section className="space-y-4">
        <h2 className="font-medium">Today’s Status</h2>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Water</span>
            <span>{progressForDate?.water_consumed_ml || 0} / {goalForDate?.water_goal_ml || 0} ml</span>
          </div>
          <Progress value={waterPct} />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Food</span>
            <span>{progressForDate?.food_consumed_g || 0} / {goalForDate?.food_goal_g || 0} g</span>
          </div>
          <Progress value={foodPct} />
        </div>
        {!goalForDate && <p className="text-sm text-gray-500">No goal set for this date yet.</p>}
      </section>
    </div>
  );
}

export default IntakeTrackerPage;
